import { useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { useMemo } from "react";

const GET_LINEAR_ISSUES_QUERY = gql(`
  query GetLinearIssues($identifiers: [String!]!) {
    getLinearIssues(identifiers: $identifiers) {
      issues {
        identifier
        title
        status
        url
        assigneeName
        isReleased
      }
      warnings
      errors
    }
  }
`);

interface LinearIssueInfo {
  identifier: string;
  title: string;
  status: string;
  url: string;
  assigneeName: string | null;
  isReleased: boolean;
}

export interface LinearIssueStatusMap {
  [identifier: string]: {
    title: string;
    status: string;
    url: string;
    assigneeName: string | null;
    isReleased: boolean;
  };
}

/**
 * Hook to fetch Linear issue statuses and URLs for tags matching "ENG-[number]" pattern
 * Returns a map of identifier -> { status, url } and handles caching
 */
export function useLinearIssueStatuses(tags: string[]): {
  statusMap: LinearIssueStatusMap;
  loading: boolean;
  error: Error | undefined;
} {
  // Extract Linear issue identifiers from tags (matching "ENG-[number]" pattern)
  // Sort to ensure consistent cache keys for Apollo
  const identifiers = useMemo(() => {
    const engTagPattern = /^ENG-\d+$/i; // Case-insensitive to match isLinearIssue
    const extracted = tags
      .filter((tag) => engTagPattern.test(tag))
      .map((tag) => tag.toUpperCase()); // Normalize to uppercase

    // Remove duplicates and sort for consistent cache keys
    return Array.from(new Set(extracted)).sort();
  }, [tags]);

  // Only query if we have identifiers
  const { data, loading, error } = useQuery<{
    getLinearIssues: {
      issues: LinearIssueInfo[];
      warnings: string[] | null;
      errors: string[] | null;
    };
  }>(GET_LINEAR_ISSUES_QUERY, {
    variables: { identifiers },
    skip: identifiers.length === 0,
    fetchPolicy: "cache-and-network", // Use cache for immediate display, but fetch fresh data
  });

  // Build map of identifier -> { title, status, url, assigneeName, isReleased }
  // Use uppercase keys for case-insensitive lookups
  const statusMap = useMemo(() => {
    const map: LinearIssueStatusMap = {};
    if (data?.getLinearIssues?.issues) {
      for (const issue of data.getLinearIssues.issues) {
        // Store with uppercase key for consistent lookups
        map[issue.identifier.toUpperCase()] = {
          title: issue.title,
          status: issue.status,
          url: issue.url,
          assigneeName: issue.assigneeName,
          isReleased: issue.isReleased,
        };
      }
    }
    return map;
  }, [data]);

  return {
    statusMap,
    loading,
    error: error as Error | undefined,
  };
}
